// deployment_class: customer_env_allowed
// @kathedral-criticality: critical
/**
 * RichText — structural elimination of the `dangerouslySetInnerHTML` class
 * across web/v3/src (DMT Area 11 · XSS-1..4).
 *
 * WHY THIS IS NOT A SANITIZER. The 13 text sinks that used to spread
 * `dangerouslySetInnerHTML` render a tiny vocabulary. It was derived
 * mechanically from every string literal under web/v3/src (comments stripped),
 * not hand-authored: `b`=140, `span`=42, `br`=6 — and nothing else. Given a
 * vocabulary that small, the choice is between filtering hostile markup on its
 * way INTO an innerHTML sink, or not having the sink at all. We do the latter:
 * RichText emits real React elements, and React escapes every text node it
 * renders. Injection is therefore inert BY CONSTRUCTION — a `<script>` payload
 * renders as the visible characters `<script>`, because there is no HTML parse
 * step anywhere in the path.
 *
 * That distinction matters. A sanitizer is a filter that must be right on every
 * input forever; this is an absence of capability. It also means we do not
 * hand-roll an HTML parser in the trust path, which is the one thing the field
 * consistently says not to do.
 *
 * EXTENDING THE VOCABULARY is a deliberate act: add the tag to ALLOWED_TAGS and
 * pair it with a test. A tag that is not allowlisted renders as literal text —
 * a visible bug, never a hole.
 */
import { Fragment, type ReactNode } from 'react';

/**
 * The derived vocabulary. `br` is void; the others wrap content.
 * Deliberately exact — see the module header on extending it.
 */
const ALLOWED_TAGS = ['b', 'span', 'br'] as const;
const VOID_TAGS = new Set<string>(['br']);

/** Only `class`, only on `span`, and only inert token characters. */
const CLASS_VALUE = /^[A-Za-z0-9 _-]*$/;

/**
 * Recognises ONLY the allowlisted constructs. Every other `<` in the input is
 * ordinary text — which is precisely why a payload cannot become an element.
 */
const TAG_RE = new RegExp(`<(/?)(${ALLOWED_TAGS.join('|')})((?:\\s[^<>]*)?)\\s*/?>`, 'gi');

interface Frame {
  tag: string;
  className?: string;
  children: ReactNode[];
}

/** Pull a safe `class` value out of an attribute string; ignore all else. */
function classNameFrom(attrs: string): string | undefined {
  const m = /(?:^|\s)class\s*=\s*("([^"]*)"|'([^']*)')/i.exec(attrs);
  const raw = m?.[2] ?? m?.[3];
  if (raw === undefined) return undefined;
  return CLASS_VALUE.test(raw) ? raw : undefined;
}

function element(frame: Frame, key: number): ReactNode {
  if (frame.tag === 'span') {
    return (
      <span key={key} className={frame.className}>
        {frame.children}
      </span>
    );
  }
  return <b key={key}>{frame.children}</b>;
}

/**
 * Parse the restricted vocabulary into React nodes. Unknown markup, stray
 * closing tags and unclosed tags all degrade to text/content rather than
 * dropping the author's copy.
 */
function parseRichText(value: string): ReactNode[] {
  const root: ReactNode[] = [];
  const stack: Frame[] = [];
  const top = (): ReactNode[] => (stack.length ? stack[stack.length - 1].children : root);

  let cursor = 0;
  let key = 0;
  TAG_RE.lastIndex = 0;

  for (let m = TAG_RE.exec(value); m !== null; m = TAG_RE.exec(value)) {
    const [raw, slash, rawTag, attrs = ''] = m;
    const tag = rawTag.toLowerCase();

    if (m.index > cursor) top().push(value.slice(cursor, m.index));
    cursor = m.index + raw.length;

    if (VOID_TAGS.has(tag)) {
      top().push(<br key={key++} />);
      continue;
    }

    if (slash === '') {
      stack.push({ tag, className: tag === 'span' ? classNameFrom(attrs) : undefined, children: [] });
      continue;
    }

    // Closing tag: honour it only if it matches the open frame, else it is text.
    const openIdx = [...stack].reverse().findIndex((f) => f.tag === tag);
    if (openIdx === -1) {
      top().push(raw);
      continue;
    }
    const frame = stack.pop() as Frame;
    top().push(element(frame, key++));
  }

  if (cursor < value.length) top().push(value.slice(cursor));

  // Unclosed tags: keep the content rather than discarding the author's copy.
  while (stack.length) {
    const frame = stack.pop() as Frame;
    top().push(element(frame, key++));
  }

  return root;
}

/**
 * Render author-supplied inline copy without an innerHTML sink.
 * Drop-in replacement for the former `{...html(value)}` spread.
 */
export function RichText({
  value,
  className,
}: {
  value: string;
  className?: string;
}): JSX.Element {
  const nodes = parseRichText(value);
  if (className === undefined) return <Fragment>{nodes}</Fragment>;
  return <div className={className}>{nodes}</div>;
}
